import { IconInnerShadowTop } from '@tabler/icons-react'
import * as React from 'react'

import { NavMain } from '@/components/layout/nav-main'
// import { NavSecondary } from '@/components/layout/nav-secondary'
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useAuth } from '@/features/authentication/components/auth-context.tsx'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { navItems } from '@/lib/route-mapping'
import { useSupervisorAccess } from '@/lib/supervisor-access'

import { NavCollapsible } from '../nav-collapsible'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth()
  const showAdminRoutes = isAdminUser(user)
  const isOrganisationAdmin = isOrganisationAdminUser(user)
  const supervisorAccessQuery = useSupervisorAccess()
  const isSupervisor = Boolean(supervisorAccessQuery.data)

  const canAccessRole = React.useCallback((roles?: Array<'admin' | 'orgAdmin' | 'supervisor'>) => {
    if (!roles?.length) {
      return true
    }
    return roles.some((role) => {
      if (role === 'admin')
        return showAdminRoutes
      if (role === 'orgAdmin')
        return isOrganisationAdmin
      if (role === 'supervisor')
        return isSupervisor
      return false
    })
  }, [isOrganisationAdmin, isSupervisor, showAdminRoutes])

  const mainNavItems = React.useMemo(() => {
    return navItems.navMain.filter(item => canAccessRole(item.roles))
  }, [canAccessRole])

  const collapsibleNavItems = React.useMemo(() => {
    return navItems.navCollapsible
      .map((section) => {
        const filteredItems = section.items?.filter(item => canAccessRole(item.roles)) ?? []
        const sectionVisible = canAccessRole(section.roles) || filteredItems.length > 0
        if (!sectionVisible || filteredItems.length === 0) {
          return null
        }
        return { ...section, items: filteredItems }
      })
      .filter((section): section is NonNullable<typeof section> => section !== null)
  }, [canAccessRole])

  const settingsNavItems = React.useMemo(() => {
    return navItems.navSettings
      .map((section) => {
        const filteredItems = section.items?.filter(item => canAccessRole(item.roles)) ?? []
        const sectionVisible = canAccessRole(section.roles) || filteredItems.length > 0
        if (!sectionVisible || filteredItems.length === 0) {
          return null
        }
        return { ...section, items: filteredItems }
      })
      .filter((section): section is NonNullable<typeof section> => section !== null)
  }, [canAccessRole])

  return (
    <>
      {/* Use icon collapse so sidebar reserves space on desktop (prevents content overlap). */}
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                className="data-[slot=sidebar-menu-button]:!p-1.5"
              >
                <a href="#">
                  <IconInnerShadowTop className="!size-5" />
                  <span className="text-base font-semibold">UIBFS Leave</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <NavMain items={mainNavItems} />
          {collapsibleNavItems.length > 0
            ? (
              <NavCollapsible items={collapsibleNavItems} />
            )
            : null}
          {settingsNavItems.length > 0
            ? (
              <NavCollapsible items={settingsNavItems} />
            )
            : null}

          {/* <NavSecondary items={navItems.navSecondary} className="mt-auto" /> */}
        </SidebarContent>
      </Sidebar>
    </>
  )
}
